import { useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { MoreVertical, Plus, Users } from "lucide-react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@pushpress/pushpress-ui";
import TablePagination from "../../shared/TablePagination";
import TableSkeleton from "../../shared/TableSkeleton";
import { PAGINATION_PAGE_SIZE } from "../../../constants";
import SectionTitle from "../../shared/SectionTitle";
import EmptyTable from "../../shared/EmptyTable";
import SubAccountsErrorState from "./SubAccountsErrorState";
import type { V2Member } from "../../../../../apis/userApi";

const convertToTableData = (profileData?: V2Member): TableRow[] => {
  if (!profileData || !profileData.linkedAccounts) return [];
  /* Filtering on clientUuid is needed because linkedAccounts
  can contain sub-accounts for other clients */
  return profileData?.linkedAccounts
    ?.filter((subAccount) => subAccount.clientUuid === profileData.clientUuid)
    .map((subAccount) => ({
      userUuid: subAccount.userUuid,
      name: `${subAccount.firstName} ${subAccount.lastName}`,
      email: subAccount.email,
    }));
};

type SubAccountsSectionProps = {
  userUuid: string;
  profileData?: V2Member;
  isProfileError: boolean;
  hasData: boolean;
  handleOpenModal: () => void;
  setSelectedSubAccountUserUuid: (userUuid: string) => void;
};

type TableRow = {
  userUuid: string;
  name: string;
  email: string;
};

export default function SubAccountsSection({
  userUuid,
  profileData,
  isProfileError,
  hasData,
  handleOpenModal,
  setSelectedSubAccountUserUuid,
}: SubAccountsSectionProps) {
  const history = useHistory();

  const handleRemoveParentUser = useCallback(
    (subAccountUserUuid: string) => {
      setSelectedSubAccountUserUuid(subAccountUserUuid);
      handleOpenModal();
    },
    [handleOpenModal, setSelectedSubAccountUserUuid],
  );

  const tableData = useMemo(
    () => convertToTableData(profileData),
    [profileData],
  );

  const columns = useMemo(
    () => [
      {
        header: "Name",
        accessorKey: "name",
        cell: ({ row }) => {
          return (
            <TableCell
              key={row.id}
              className="hover:tw-text-primary tw-cursor-pointer"
            >
              {row.original.name}
            </TableCell>
          );
        },
      },
      {
        header: "Email",
        accessorKey: "email",
        cell: ({ row }) => {
          return (
            <TableCell
              key={row.id}
              className="hover:tw-text-primary tw-cursor-pointer"
            >
              {row.original.email}
            </TableCell>
          );
        },
      },
      {
        id: "actions",
        accessorKey: "actions",
        header: null,
        cell: ({ row }) => (
          <div className="tw-flex tw-justify-end tw-mr-1 max-sm:tw-w-[20px]">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" data-testid="row-actions-button">
                  <MoreVertical className="tw-h-5 tw-w-5 max-sm:tw-p-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => {
                    history.push(`/user/${row.original.userUuid}`);
                  }}
                >
                  View sub-account
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleRemoveParentUser(row.original.userUuid)}
                >
                  Remove from parent account
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      },
    ],
    [history, handleRemoveParentUser],
  );

  const table = useReactTable({
    data: tableData,
    columns,
    initialState: {
      pagination: {
        pageSize: PAGINATION_PAGE_SIZE,
      },
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const showPagination = hasData && tableData?.length > PAGINATION_PAGE_SIZE;

  return (
    <div className="tw-rounded-lg tw-border tw-p-4 tw-bg-white tw-flex tw-flex-col tw-gap-4">
      <div className="tw-flex tw-justify-between">
        <SectionTitle icon={Users} title="Sub-accounts" />
        <Button
          size="sm"
          onClick={() => {
            window.location.href = `/members/add_subaccount/${userUuid}`;
          }}
        >
          <Plus className="tw-mr-1 tw-h-4 tw-w-4" />
          Add Sub-account
        </Button>
      </div>
      <div className="tw-rounded tw-ring-1 tw-ring-black/5">
        {isProfileError ? (
          <SubAccountsErrorState />
        ) : !hasData ? (
          <TableSkeleton numCols={2} numRows={3} />
        ) : (
          <Table className="tw-divide-y tw-divide-neutral-300">
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id} className="tw-bg-neutral-50">
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody className="tw-divide-y tw-divide-neutral-200 tw-bg-white">
              {table.getRowModel()?.rows?.length ? (
                table.getRowModel().rows?.map((row) => (
                  <TableRow
                    key={row.id}
                    className="tw-py-0 tw-cursor-pointer tw-h-14"
                  >
                    {row.getVisibleCells().map((cell) => {
                      const isClickable = cell.column.id !== "actions";
                      return (
                        <TableCell
                          key={cell.id}
                          className="tw-py-0"
                          onClick={() => {
                            if (isClickable) {
                              history.push(`/user/${row.original.userUuid}`);
                            }
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              ) : (
                <EmptyTable
                  colLength={columns.length}
                  userUuid={userUuid}
                  title="No sub-accounts for this user"
                  subtitle="Sub-account is a secondary profile tied to this account."
                  action={
                    <Button
                      onClick={() => {
                        window.location.href = `/members/add_subaccount/${userUuid}`;
                      }}
                      size="sm"
                    >
                      <Plus className="tw-mr-1 tw-h-4 tw-w-4" />
                      Add Sub-account
                    </Button>
                  }
                />
              )}
            </TableBody>
          </Table>
        )}
      </div>
      {showPagination && <TablePagination table={table} />}
    </div>
  );
}
